import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	DragDropContext,
	Droppable,
	Draggable,
	type DropResult,
} from "@hello-pangea/dnd";
import {
	Menu,
	Plus,
	Edit2,
	Trash2,
	ChevronRight,
	ChevronDown,
	GripVertical,
	EyeOff,
	Save,
	ArrowUp,
	ArrowDown,
	Eye,
	LayoutDashboard,
	Settings,
	Users,
	FileText,
	Shield,
	BarChart3,
	Package,
	Building2,
	CreditCard,
	Activity,
	Home,
	Bell,
	Calendar,
	Clock,
	Cloud,
	Code,
	Database,
	Download,
	Upload,
	File,
	Folder,
	Globe,
	Heart,
	Image,
	Info,
	Key,
	Layers,
	Link,
	Lock,
	Mail,
	Map as MapIcon,
	MessageSquare,
	Monitor,
	Moon,
	Sun,
	Palette,
	PieChart,
	Plug,
	Printer,
	Search,
	Server,
	Share2,
	ShoppingCart,
	Smartphone,
	Star,
	Tag,
	Terminal,
	Thermometer,
	Wrench,
	TrendingUp,
	Truck,
	Tv,
	User,
	UserCheck,
	UserPlus,
	Video,
	Wifi,
	Zap,
	BookOpen,
	Briefcase,
	Camera,
	Clipboard,
	Compass,
	Cpu,
	DollarSign,
	Feather,
	Filter,
	Flag,
	Gift,
	HardDrive,
	Headphones,
	Inbox,
	LifeBuoy,
	LogOut,
	MapPin,
	Megaphone,
	Navigation,
	Paperclip,
	Phone,
	Power,
	Radio,
	RefreshCw,
	Repeat,
	Rocket,
	RotateCcw,
	Rss,
	Scissors,
	Send,
	Sliders,
	Speaker,
	Target,
	Trello,
	Umbrella,
	Unlock,
	Volume2,
	Watch,
	Wind,
	Award,
	BarChart2,
	Box,
	Cast,
	CheckCircle,
	Chrome,
	Columns,
	Copy,
	Crosshair,
	Disc,
	Edit3,
	ExternalLink,
	FileCheck,
	FileMinus,
	FilePlus,
	FolderOpen,
	FolderPlus,
	Gitlab,
	Grid,
	Hash,
	HelpCircle,
	Hexagon,
	Layout,
	List,
	Loader,
	LogIn,
	Maximize,
	Minimize,
	MoreHorizontal,
	Move,
	Music,
	Octagon,
	PenTool,
	Percent,
	PhoneCall,
	PlayCircle,
	PlusCircle,
	Pocket,
	ShieldOff,
	Sidebar,
	SkipBack,
	Square,
	Sunrise,
	Table,
	Tablet,
	ThumbsUp,
	ToggleLeft,
	Type,
	UserMinus,
	Users2,
	Voicemail,
	XCircle,
	AlertCircle,
	AlertTriangle,
	Archive,
	AtSign,
	Battery,
	Bluetooth,
	Bold,
	Bookmark,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/toaster";
import * as api from "@/services/api";

interface MenuItem {
	_id: string;
	code: string;
	label: string;
	icon?: string;
	path?: string;
	parentCode?: string;
	order: number;
	featureCode?: string;
	isHidden?: boolean;
	isSection?: boolean;
	isDivider?: boolean;
	children?: MenuItem[];
}

// Icon mapping for preview
const iconMap: Record<string, React.ElementType> = {
	// Original icons
	dashboard: LayoutDashboard,
	settings: Settings,
	users: Users,
	"file-text": FileText,
	shield: Shield,
	"bar-chart": BarChart3,
	package: Package,
	building: Building2,
	"credit-card": CreditCard,
	activity: Activity,
	menu: Menu,
	// Navigation & Layout
	home: Home,
	layout: Layout,
	sidebar: Sidebar,
	grid: Grid,
	columns: Columns,
	list: List,
	table: Table,
	layers: Layers,
	// Communication
	bell: Bell,
	mail: Mail,
	"message-square": MessageSquare,
	phone: Phone,
	"phone-call": PhoneCall,
	send: Send,
	inbox: Inbox,
	megaphone: Megaphone,
	voicemail: Voicemail,
	"at-sign": AtSign,
	// Media
	image: Image,
	camera: Camera,
	video: Video,
	music: Music,
	"play-circle": PlayCircle,
	volume: Volume2,
	headphones: Headphones,
	speaker: Speaker,
	radio: Radio,
	tv: Tv,
	monitor: Monitor,
	// Files & Documents
	file: File,
	"file-check": FileCheck,
	"file-plus": FilePlus,
	"file-minus": FileMinus,
	folder: Folder,
	"folder-open": FolderOpen,
	"folder-plus": FolderPlus,
	clipboard: Clipboard,
	"book-open": BookOpen,
	paperclip: Paperclip,
	archive: Archive,
	// Users & People
	user: User,
	"user-check": UserCheck,
	"user-plus": UserPlus,
	"user-minus": UserMinus,
	"users-2": Users2,
	// Business & Finance
	briefcase: Briefcase,
	"dollar-sign": DollarSign,
	"shopping-cart": ShoppingCart,
	"bar-chart-2": BarChart2,
	"pie-chart": PieChart,
	"trending-up": TrendingUp,
	percent: Percent,
	// Technology
	code: Code,
	terminal: Terminal,
	database: Database,
	server: Server,
	"hard-drive": HardDrive,
	cpu: Cpu,
	cloud: Cloud,
	wifi: Wifi,
	bluetooth: Bluetooth,
	smartphone: Smartphone,
	tablet: Tablet,
	plug: Plug,
	cast: Cast,
	globe: Globe,
	chrome: Chrome,
	// Security
	lock: Lock,
	unlock: Unlock,
	key: Key,
	"shield-off": ShieldOff,
	// Time & Date
	calendar: Calendar,
	clock: Clock,
	watch: Watch,
	// Actions
	search: Search,
	filter: Filter,
	download: Download,
	upload: Upload,
	"refresh-cw": RefreshCw,
	"rotate-ccw": RotateCcw,
	repeat: Repeat,
	share: Share2,
	link: Link,
	"external-link": ExternalLink,
	copy: Copy,
	move: Move,
	maximize: Maximize,
	minimize: Minimize,
	printer: Printer,
	"log-in": LogIn,
	"log-out": LogOut,
	// Shapes & Symbols
	heart: Heart,
	star: Star,
	flag: Flag,
	bookmark: Bookmark,
	tag: Tag,
	hash: Hash,
	"plus-circle": PlusCircle,
	"check-circle": CheckCircle,
	"x-circle": XCircle,
	"alert-circle": AlertCircle,
	"alert-triangle": AlertTriangle,
	"help-circle": HelpCircle,
	info: Info,
	zap: Zap,
	target: Target,
	crosshair: Crosshair,
	hexagon: Hexagon,
	octagon: Octagon,
	square: Square,
	disc: Disc,
	// Tools & Objects
	tool: Wrench,
	sliders: Sliders,
	"pen-tool": PenTool,
	edit: Edit3,
	scissors: Scissors,
	feather: Feather,
	compass: Compass,
	map: MapIcon,
	"map-pin": MapPin,
	navigation: Navigation,
	thermometer: Thermometer,
	// Misc
	gift: Gift,
	award: Award,
	rocket: Rocket,
	"life-buoy": LifeBuoy,
	umbrella: Umbrella,
	truck: Truck,
	box: Box,
	pocket: Pocket,
	rss: Rss,
	gitlab: Gitlab,
	trello: Trello,
	power: Power,
	battery: Battery,
	loader: Loader,
	"toggle-left": ToggleLeft,
	"more-horizontal": MoreHorizontal,
	"thumbs-up": ThumbsUp,
	bold: Bold,
	type: Type,
	palette: Palette,
	sun: Sun,
	moon: Moon,
	sunrise: Sunrise,
	wind: Wind,
	"skip-back": SkipBack,
};

export function MenuBuilder() {
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
	const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [showHidden, setShowHidden] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [previewExpandedSections, setPreviewExpandedSections] = useState<Set<string>>(new Set());
	const [createDefaults, setCreateDefaults] = useState<{
		parentCode?: string;
		order?: number;
	} | null>(null);
	const queryClient = useQueryClient();

	const { data: menuStructure, isLoading } = useQuery({
		queryKey: ["menu-structure"],
		queryFn: api.getMenuStructure,
	});

	const { data: allItems } = useQuery({
		queryKey: ["menu-items", showHidden],
		queryFn: () => api.getMenuItems(showHidden),
	});

	const { data: sections } = useQuery({
		queryKey: ["menu-sections"],
		queryFn: api.getMenuSections,
	});

	const { data: features } = useQuery({
		queryKey: ["features"],
		queryFn: api.getFeatures,
	});

	const { data: roles } = useQuery({
		queryKey: ["roles"],
		queryFn: () => api.getRoles(),
	});

	const [previewRoleId, setPreviewRoleId] = useState<string>("");

	const { data: previewData } = useQuery({
		queryKey: ["menu-preview", previewRoleId],
		queryFn: () => api.getMenuPreview(previewRoleId || undefined),
	});

	// Expand all sections by default on initial load only
	const hasInitializedExpanded = useRef(false);
	useEffect(() => {
		if (allItems && !hasInitializedExpanded.current) {
			hasInitializedExpanded.current = true;
			const sectionCodes = allItems.filter(i => i.isSection).map(i => i.code);
			setExpandedItems(new Set(sectionCodes));
		}
	}, [allItems]);

	const reorderMutation = useMutation({
		mutationFn: api.reorderMenuItems,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu-structure"] });
			queryClient.invalidateQueries({ queryKey: ["menu-items"] });
			toast({ title: "Order updated", variant: "success" });
		},
		onError: (error: Error) => {
			toast({ title: "Error", description: error.message, variant: "destructive" });
		},
	});

	const createMutation = useMutation({
		mutationFn: api.createMenuItem,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu-structure"] });
			queryClient.invalidateQueries({ queryKey: ["menu-items"] });
			setIsCreateDialogOpen(false);
			setCreateDefaults(null);
			toast({ title: "Menu item created", variant: "success" });
		},
		onError: (error: Error) => {
			toast({ title: "Error", description: error.message, variant: "destructive" });
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({ code, data }: { code: string; data: any }) =>
			api.updateMenuItem(code, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu-structure"] });
			queryClient.invalidateQueries({ queryKey: ["menu-items"] });
			setEditingItem(null);
			toast({ title: "Menu item updated", variant: "success" });
		},
		onError: (error: Error) => {
			toast({ title: "Error", description: error.message, variant: "destructive" });
		},
	});

	const deleteMutation = useMutation({
		mutationFn: api.deleteMenuItem,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu-structure"] });
			queryClient.invalidateQueries({ queryKey: ["menu-items"] });
			toast({ title: "Menu item deleted", variant: "success" });
		},
		onError: (error: Error) => {
			toast({ title: "Error", description: error.message, variant: "destructive" });
		},
	});

	const toggleExpanded = (code: string) => {
		setExpandedItems((prev) => {
			const next = new Set(prev);
			if (next.has(code)) {
				next.delete(code);
			} else {
				next.add(code);
			}
			return next;
		});
	};

	const togglePreviewSection = (section: string) => {
		setPreviewExpandedSections((prev) => {
			const next = new Set(prev);
			if (next.has(section)) {
				next.delete(section);
			} else {
				next.add(section);
			}
			return next;
		});
	};

	const handleDelete = (code: string, label: string) => {
		if (window.confirm(`Delete "${label}"?`)) {
			deleteMutation.mutate(code);
		}
	};

	// Get siblings for reordering (items with the same parentCode, or sections)
	const getSiblings = (item: MenuItem): MenuItem[] => {
		// For section items, siblings are other sections
		if (item.isSection || item.isDivider) {
			if (!sections && !allItems) return [];
			return [...(sections || []), ...(allItems?.filter(i => i.isDivider) || [])]
				.map((s) => ({
					_id: s.code,
					code: s.code,
					label: s.label,
					icon: s.icon,
					order: s.order,
					isSection: s.isSection || false,
					isDivider: s.isDivider || false,
				} as MenuItem))
				.sort((a, b) => a.order - b.order);
		}
		// For regular items, siblings are items with the same parentCode
		if (!allItems) return [];
		return allItems
			.filter((i) => i.parentCode === item.parentCode && !i.isSection)
			.sort((a, b) => a.order - b.order);
	};

	const handleMoveUp = (item: MenuItem) => {
		const siblings = getSiblings(item);
		const currentIndex = siblings.findIndex((s) => s.code === item.code);
		if (currentIndex <= 0) return;

		const prevItem = siblings[currentIndex - 1];
		reorderMutation.mutate([
			{ code: item.code, order: prevItem.order },
			{ code: prevItem.code, order: item.order },
		]);
	};

	const handleMoveDown = (item: MenuItem) => {
		const siblings = getSiblings(item);
		const currentIndex = siblings.findIndex((s) => s.code === item.code);
		if (currentIndex >= siblings.length - 1) return;

		const nextItem = siblings[currentIndex + 1];
		reorderMutation.mutate([
			{ code: item.code, order: nextItem.order },
			{ code: nextItem.code, order: item.order },
		]);
	};

	const canMoveUp = (item: MenuItem): boolean => {
		const siblings = getSiblings(item);
		const currentIndex = siblings.findIndex((s) => s.code === item.code);
		return currentIndex > 0;
	};

	const canMoveDown = (item: MenuItem): boolean => {
		const siblings = getSiblings(item);
		const currentIndex = siblings.findIndex((s) => s.code === item.code);
		return currentIndex < siblings.length - 1;
	};

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination) return;
		if (
			result.source.index === result.destination.index &&
			result.source.droppableId === result.destination.droppableId
		)
			return;

		const droppableId = result.source.droppableId;

		// Determine which siblings list we're reordering
		let siblings: MenuItem[];
		if (droppableId === "sections") {
			// Reordering top-level items (sections, dividers, and root items interleaved)
			if (!allItems) return;
			siblings = allItems
				.filter((i) => !i.parentCode)
				.sort((a, b) => a.order - b.order);
		} else {
			// Reordering children within a section (droppableId = parentCode)
			if (!allItems) return;
			siblings = allItems
				.filter((i) => i.parentCode === droppableId && !i.isSection)
				.sort((a, b) => a.order - b.order);
		}

		// Reorder the array
		const reordered = [...siblings];
		const [movedItem] = reordered.splice(result.source.index, 1);
		reordered.splice(result.destination.index, 0, movedItem);

		// Recalculate order values (use index * 10 for spacing)
		const reorderItems = reordered.map((item, index) => ({
			code: item.code,
			order: (index + 1) * 10,
		}));

		reorderMutation.mutate(reorderItems);
	};

	// Type for hierarchical menu items (with required children array)
	type HierarchicalMenuItem = Omit<MenuItem, 'children'> & { children: HierarchicalMenuItem[] };

	// Build hierarchical menu from allItems - SAME LOGIC AS BACKEND
	const buildHierarchicalMenu = (): MenuItem[] => {
		if (!allItems) return [];

		// Step 1: Get all visible items
		const visibleItems = allItems.filter(item => !item.isHidden || showHidden);

		// Step 2: Create a map of all items
		const itemMap = new Map<string, HierarchicalMenuItem>();
		for (const item of visibleItems) {
			itemMap.set(item.code, { ...item, children: [] });
		}

		// Step 3: Build parent-child relationships
		const topLevelItems: HierarchicalMenuItem[] = [];

		for (const item of visibleItems) {
			const mappedItem = itemMap.get(item.code);
			if (!mappedItem) continue;

			if (item.parentCode && itemMap.has(item.parentCode)) {
				// Has a parent - add to parent's children
				const parent = itemMap.get(item.parentCode);
				if (parent) {
					parent.children.push(mappedItem);
				}
			} else {
				// No parent or parent not visible - top level
				topLevelItems.push(mappedItem);
			}
		}

		// Step 4: Sort by order at all levels
		const sortByOrder = (items: HierarchicalMenuItem[]) => {
			items.sort((a, b) => a.order - b.order);
			for (const item of items) {
				if (item.children.length > 0) {
					sortByOrder(item.children);
				}
			}
		};
		sortByOrder(topLevelItems);

		// Step 5: Convert to MenuItem[] (keep empty sections for tree editor)
		const toMenuItems = (items: HierarchicalMenuItem[]): MenuItem[] => {
			return items.map(item => ({
				...item,
				children: item.children.length > 0 ? toMenuItems(item.children) : undefined,
			}));
		};

		return toMenuItems(topLevelItems);
	};

	// InsertionPoint component - clickable "+" line between items
	const InsertionPoint = ({ parentCode, order }: { parentCode?: string; order: number }) => (
		<div
			className="group/insert relative h-2 -my-0.5 flex items-center cursor-pointer"
			onClick={() => {
				setCreateDefaults({ parentCode, order });
				setIsCreateDialogOpen(true);
			}}
		>
			<div className="absolute inset-x-2 h-0.5 bg-blue-400 rounded opacity-0 group-hover/insert:opacity-100 transition-opacity" />
			<div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-1/2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center opacity-0 group-hover/insert:opacity-100 transition-opacity z-10">
				<Plus className="w-3 h-3 text-white" />
			</div>
		</div>
	);

	// Visual tree renderer
	const renderTreeItem = (item: MenuItem, depth = 0) => {
		const isExpanded = expandedItems.has(item.code);
		const IconComponent = iconMap[item.icon?.toLowerCase() || ""] || Menu;

		// --- DIVIDER ---
		if (item.isDivider) {
			return (
				<div key={item.code} className="group relative">
					<div className="flex items-center gap-2 py-3 px-2">
						<div className="flex items-center gap-2 flex-1">
							<GripVertical className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 cursor-grab" />
							<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
								{item.label}
							</span>
							<div className="flex-1 border-b border-gray-200" />
						</div>
						<div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
							<code className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
								{item.code}
							</code>
							<Badge variant="outline" className="text-[10px] text-orange-500 border-orange-200">
								Divider
							</Badge>
							<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingItem(item)}>
								<Edit2 className="w-3 h-3" />
							</Button>
							<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(item.code, item.label)}>
								<Trash2 className="w-3 h-3 text-red-400" />
							</Button>
						</div>
					</div>
				</div>
			);
		}

		// --- SECTION (collapsible group) ---
		if (item.isSection) {
			const childCount = item.children?.length || 0;
			return (
				<div key={item.code} className="group/section">
					<div className="flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-gray-50 cursor-pointer">
						<GripVertical className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover/section:opacity-100 cursor-grab" />
						<button
							onClick={() => toggleExpanded(item.code)}
							className="flex items-center gap-2 flex-1 text-left"
						>
							{isExpanded ? (
								<ChevronDown className="w-4 h-4 text-gray-400" />
							) : (
								<ChevronRight className="w-4 h-4 text-gray-400" />
							)}
							<IconComponent className="w-4 h-4 text-gray-500" />
							<span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
								{item.label}
							</span>
							<Badge variant="secondary" className="text-[10px] ml-1">
								{childCount}
							</Badge>
						</button>
						<div className="opacity-0 group-hover/section:opacity-100 flex items-center gap-0.5">
							<code className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
								{item.code}
							</code>
							<Badge variant="outline" className="text-[10px] text-purple-500 border-purple-200">
								Section
							</Badge>
							<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingItem(item)}>
								<Edit2 className="w-3 h-3" />
							</Button>
							<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(item.code, item.label)}>
								<Trash2 className="w-3 h-3 text-red-400" />
							</Button>
						</div>
					</div>
					{isExpanded && (
						<div className="ml-6 pl-3 border-l-2 border-gray-100">
							{(() => {
								const sorted = [...(item.children || [])].sort((a, b) => a.order - b.order);
								const elements: React.ReactNode[] = [];
								elements.push(
									<InsertionPoint
										key={`insert-${item.code}-top`}
										parentCode={item.code}
										order={sorted.length > 0 ? sorted[0].order - 5 : 10}
									/>
								);
								sorted.forEach((child, idx) => {
									elements.push(
										<Draggable key={child.code} draggableId={child.code} index={idx}>
											{(childDragProv, childDragSnap) => (
												<div
													ref={childDragProv.innerRef}
													{...childDragProv.draggableProps}
													{...childDragProv.dragHandleProps}
													className={childDragSnap.isDragging ? "opacity-75" : ""}
												>
													{renderTreeItem(child, depth + 1)}
												</div>
											)}
										</Draggable>
									);
									const nextOrder = idx < sorted.length - 1 ? sorted[idx + 1].order : child.order + 10;
									elements.push(
										<InsertionPoint
											key={`insert-${item.code}-${idx}`}
											parentCode={item.code}
											order={Math.floor((child.order + nextOrder) / 2)}
										/>
									);
								});
								return elements;
							})()}
						</div>
					)}
				</div>
			);
		}

		// --- REGULAR MENU ITEM ---
		return (
			<div key={item.code} className="group relative">
				<div
					className={`flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-blue-50/50 transition-colors ${
						item.isHidden ? "opacity-40" : ""
					}`}
					style={{ paddingLeft: `${8 + depth * 16}px` }}
				>
					<GripVertical className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 cursor-grab" />
					<IconComponent className="w-4 h-4 text-gray-500" />
					<span className="text-sm font-medium text-gray-800 flex-1">{item.label}</span>
					{item.isHidden && <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
					<div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
						<code className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
							{item.code}
						</code>
						{item.path && (
							<Badge variant="outline" className="text-[10px] text-blue-500 border-blue-200">
								{item.path}
							</Badge>
						)}
						<Button variant="ghost" size="sm" className="h-6 w-6 p-0"
							onClick={() => handleMoveUp(item)} disabled={!canMoveUp(item)}>
							<ArrowUp className="w-3 h-3" />
						</Button>
						<Button variant="ghost" size="sm" className="h-6 w-6 p-0"
							onClick={() => handleMoveDown(item)} disabled={!canMoveDown(item)}>
							<ArrowDown className="w-3 h-3" />
						</Button>
						<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingItem(item)}>
							<Edit2 className="w-3 h-3" />
						</Button>
						<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(item.code, item.label)}>
							<Trash2 className="w-3 h-3 text-red-400" />
						</Button>
					</div>
				</div>
			</div>
		);
	};

	// Preview component - shows menu EXACTLY as /v2/permissions/me returns it
	// This builds hierarchy the same way the backend does
	const MenuPreview = () => {
		const getIcon = (iconName?: string) => {
			if (!iconName) return Menu;
			return iconMap[iconName.toLowerCase()] || Menu;
		};

		// Build preview menu: use role-filtered data if available, otherwise buildHierarchicalMenu
		const buildPreviewMenu = (): MenuItem[] => {
			const tree = buildHierarchicalMenu();
			// For preview, remove empty sections (same as backend cleanupMenu)
			const cleanup = (items: MenuItem[]): MenuItem[] => {
				return items
					.filter(item => {
						if (item.isSection && !item.isDivider && (!item.children || item.children.length === 0)) {
							return false;
						}
						return true;
					})
					.map(item => ({
						...item,
						children: item.children ? cleanup(item.children) : undefined,
					}));
			};
			return cleanup(tree);
		};

		const menuItems = previewRoleId && previewData ? previewData.items : buildPreviewMenu();

		const renderPreviewItem = (item: MenuItem, depth = 0) => {
			const hasChildren = item.children && item.children.length > 0;
			const isExpanded = previewExpandedSections.has(item.code);
			const IconComponent = getIcon(item.icon);

			if (item.isHidden && !showHidden) return null;

			if (item.isDivider) {
				return (
					<div key={item.code} className="pt-4 pb-1 px-3">
						<span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
							{item.label}
						</span>
						<div className="border-b border-gray-700 mt-1" />
					</div>
				);
			}

			return (
				<div key={item.code}>
					<div
						className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
							item.isSection
								? "text-gray-400 hover:text-gray-200"
								: depth === 0
									? "text-gray-300 hover:bg-gray-800 hover:text-white"
									: "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
						} ${item.isHidden ? "opacity-50" : ""}`}
						onClick={() => hasChildren && togglePreviewSection(item.code)}
						style={{ paddingLeft: `${12 + depth * 16}px` }}
					>
						<span className="text-[10px] font-mono text-gray-500 min-w-[20px] text-right">{item.order}</span>
						<IconComponent className="w-4 h-4" />
						<span className={`font-medium flex-1 ${item.isSection ? "uppercase text-xs" : ""}`}>
							{item.label}
						</span>
						{hasChildren && (
							isExpanded ? (
								<ChevronDown className="w-4 h-4" />
							) : (
								<ChevronRight className="w-4 h-4" />
							)
						)}
					</div>
					{hasChildren && isExpanded && (
						<div className="mt-1">
							{item.children!.map((child) => renderPreviewItem(child, depth + 1))}
						</div>
					)}
				</div>
			);
		};

		return (
			<div>
				{/* Role selector */}
				<div className="mb-4">
					<Select value={previewRoleId || "all"} onValueChange={(v) => setPreviewRoleId(v === "all" ? "" : v)}>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="All items (no role filter)" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All items</SelectItem>
							{roles?.map((role) => (
								<SelectItem key={role._id} value={role._id}>
									{role.displayName || role.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{previewData && previewRoleId && (
						<p className="text-xs text-gray-500 mt-1">
							Showing {previewData.visibleItems} of {previewData.totalItems} items
						</p>
					)}
				</div>
				<div className="w-64 bg-gray-900 text-white rounded-lg overflow-hidden shadow-xl">
					<div className="p-4 border-b border-gray-800">
						<h1 className="text-lg font-bold">Flow Metrics</h1>
						<p className="text-xs text-gray-400">Menu Preview (matches /v2/permissions/me)</p>
					</div>
					<nav className="p-3 space-y-1 max-h-[500px] overflow-y-auto">
						{menuItems.map((item: MenuItem) => renderPreviewItem(item))}
						{menuItems.length === 0 && (
							<p className="text-gray-500 text-sm text-center py-4">No menu items</p>
						)}
					</nav>
				</div>
			</div>
		);
	};

	const SearchableSelect = ({
		value,
		onValueChange,
		placeholder,
		disabled,
		options,
	}: {
		value: string;
		onValueChange: (v: string) => void;
		placeholder: string;
		disabled?: boolean;
		options: Array<{ value: string; label: React.ReactNode; searchText: string }>;
	}) => {
		const [open, setOpen] = useState(false);
		const [search, setSearch] = useState("");
		const containerRef = useRef<HTMLDivElement>(null);

		const filtered = search
			? options.filter((o) => o.searchText.toLowerCase().includes(search.toLowerCase()))
			: options;

		const selectedOption = options.find((o) => o.value === value);

		useEffect(() => {
			if (!open) return;
			const handleClickOutside = (e: MouseEvent) => {
				if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
					setOpen(false);
					setSearch("");
				}
			};
			document.addEventListener("mousedown", handleClickOutside);
			return () => document.removeEventListener("mousedown", handleClickOutside);
		}, [open]);

		return (
			<div ref={containerRef} className="relative">
				<button
					type="button"
					disabled={disabled}
					onClick={() => { if (!disabled) setOpen(!open); }}
					className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
				>
					<span className="truncate">
						{selectedOption ? selectedOption.label : <span className="text-muted-foreground">{placeholder}</span>}
					</span>
					<ChevronDown className="h-4 w-4 opacity-50" />
				</button>
				{open && (
					<div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
						<div className="p-2 border-b">
							<div className="flex items-center gap-2 px-2">
								<Search className="h-4 w-4 text-muted-foreground shrink-0" />
								<input
									autoFocus
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder="Search..."
									className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
								/>
							</div>
						</div>
						<div className="max-h-[200px] overflow-y-auto p-1">
							{filtered.length === 0 && (
								<p className="text-sm text-muted-foreground text-center py-4">No results found</p>
							)}
							{filtered.map((option) => (
								<button
									key={option.value}
									type="button"
									onClick={() => {
										onValueChange(option.value);
										setOpen(false);
										setSearch("");
									}}
									className={`w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer ${
										option.value === value ? "bg-accent" : ""
									}`}
								>
									{option.label}
								</button>
							))}
						</div>
					</div>
				)}
			</div>
		);
	};

	const MenuItemForm = ({
		item,
		onSubmit,
		onCancel,
		isCreate = false,
		availableFeatures,
		defaults,
	}: {
		item?: MenuItem;
		onSubmit: (data: any) => void;
		onCancel: () => void;
		isCreate?: boolean;
		availableFeatures?: Array<{ code?: string; slug: string; name?: string; module: string }>;
		defaults?: { parentCode?: string; order?: number } | null;
	}) => {
		const [formData, setFormData] = useState({
			code: item?.code || "",
			label: item?.label || "",
			icon: item?.icon || "",
			path: item?.path || "",
			parentCode: item?.parentCode || defaults?.parentCode || "",
			order: item?.order ?? defaults?.order ?? 0,
			featureCode: item?.featureCode || "",
			isHidden: item?.isHidden || false,
			isSection: item?.isSection || false,
			isDivider: item?.isDivider || false,
		});

		return (
			<div className="space-y-4">
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="text-sm font-medium">Code</label>
						<Input
							value={formData.code}
							onChange={(e) =>
								setFormData({ ...formData, code: e.target.value })
							}
							placeholder="unique_code"
							disabled={!isCreate}
						/>
					</div>
					<div>
						<label className="text-sm font-medium">Label</label>
						<Input
							value={formData.label}
							onChange={(e) =>
								setFormData({ ...formData, label: e.target.value })
							}
							placeholder="Display Label"
						/>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="text-sm font-medium">Icon</label>
						<SearchableSelect
							value={formData.icon || "_none"}
							onValueChange={(v) => setFormData({ ...formData, icon: v === "_none" ? "" : v })}
							placeholder="Select icon..."
							options={[
								{ value: "_none", label: "None", searchText: "none" },
								...Object.keys(iconMap).map((iconName) => {
									const IconComp = iconMap[iconName];
									return {
										value: iconName,
										label: (
											<span className="flex items-center gap-2">
												<IconComp className="w-4 h-4" />
												{iconName}
											</span>
										),
										searchText: iconName,
									};
								}),
							]}
						/>
					</div>
					<div>
						<label className="text-sm font-medium">Path</label>
						<Input
							value={formData.path}
							onChange={(e) =>
								setFormData({ ...formData, path: e.target.value })
							}
							placeholder="/path/to/page"
							disabled={formData.isSection || formData.isDivider}
						/>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="text-sm font-medium">Parent Code</label>
						<Select
							value={formData.parentCode || "_none"}
							onValueChange={(v) => setFormData({ ...formData, parentCode: v === "_none" ? "" : v })}
							disabled={formData.isSection || formData.isDivider}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select parent..." />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="_none">None (root)</SelectItem>
								{sections?.map((s) => (
									<SelectItem key={s.code} value={s.code}>
										📁 {s.label}
									</SelectItem>
								))}
								{allItems
									?.filter((i) => i.code !== item?.code && !i.isSection)
									.map((i) => (
										<SelectItem key={i.code} value={i.code}>
											{i.label}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
					</div>
					<div>
						<label className="text-sm font-medium">Order</label>
						<Input
							type="number"
							value={formData.order}
							onChange={(e) =>
								setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
							}
						/>
					</div>
				</div>

				<div>
					<label className="text-sm font-medium">Feature Code</label>
					<SearchableSelect
						value={formData.featureCode || "_none"}
						onValueChange={(v) => setFormData({ ...formData, featureCode: v === "_none" ? "" : v })}
						placeholder="Select feature code..."
						disabled={formData.isSection || formData.isDivider}
						options={[
							{ value: "_none", label: "None", searchText: "none" },
							...(availableFeatures?.map((f) => ({
								value: f.code || f.slug,
								label: (
									<span className="flex items-center gap-2">
										<span className="font-mono text-xs">{f.code || f.slug}</span>
										<span className="text-muted-foreground">- {f.name || f.slug} ({f.module})</span>
									</span>
								),
								searchText: `${f.code || ""} ${f.slug} ${f.name || ""} ${f.module}`,
							})) || []),
						]}
					/>
				</div>

				<div className="flex items-center gap-4">
					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							id="isSection"
							checked={formData.isSection}
							disabled={formData.isDivider}
							onChange={(e) =>
								setFormData({
									...formData,
									isSection: e.target.checked,
									// Clear path, parentCode, featureCode when marking as section
									...(e.target.checked ? { path: "", parentCode: "", featureCode: "" } : {})
								})
							}
						/>
						<label htmlFor="isSection" className="text-sm font-medium">
							Is Section (folder header)
						</label>
					</div>
					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							id="isHidden"
							checked={formData.isHidden}
							onChange={(e) =>
								setFormData({ ...formData, isHidden: e.target.checked })
							}
						/>
						<label htmlFor="isHidden" className="text-sm">
							Hidden
						</label>
					</div>
					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							id="isDivider"
							checked={formData.isDivider}
							onChange={(e) =>
								setFormData({
									...formData,
									isDivider: e.target.checked,
									...(e.target.checked ? { path: "", parentCode: "", featureCode: "", isSection: false } : {})
								})
							}
						/>
						<label htmlFor="isDivider" className="text-sm font-medium">
							Is Divider (visual separator)
						</label>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onCancel}>
						Cancel
					</Button>
					<Button onClick={() => onSubmit(formData)}>
						<Save className="w-4 h-4 mr-2" />
						{isCreate ? "Create" : "Save"}
					</Button>
				</DialogFooter>
			</div>
		);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Menu Builder</h1>
					<p className="text-gray-500 mt-1">
						Configure navigation menu structure
					</p>
				</div>
				<div className="flex items-center gap-3">
					<label className="flex items-center gap-2 text-sm">
						<input
							type="checkbox"
							checked={showHidden}
							onChange={(e) => setShowHidden(e.target.checked)}
							className="rounded border-gray-300"
						/>
						Show hidden
					</label>
					<Button
						variant="outline"
						onClick={() => setShowPreview(!showPreview)}
					>
						<Eye className="w-4 h-4 mr-2" />
						{showPreview ? "Hide Preview" : "Show Preview"}
					</Button>
					<Button onClick={() => { setCreateDefaults(null); setIsCreateDialogOpen(true); }}>
						<Plus className="w-4 h-4 mr-2" />
						Add Item
					</Button>
				</div>
			</div>

			{/* Stats */}
			<div className="grid grid-cols-4 gap-4">
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">
							{menuStructure?.totalItems || 0}
						</div>
						<p className="text-sm text-muted-foreground">Total Items</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">
							{menuStructure?.sections?.length || 0}
						</div>
						<p className="text-sm text-muted-foreground">Sections</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">
							{allItems?.filter((i) => i.isHidden).length || 0}
						</div>
						<p className="text-sm text-muted-foreground">Hidden Items</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">
							{allItems?.filter((i) => i.isDivider).length || 0}
						</div>
						<p className="text-sm text-muted-foreground">Dividers</p>
					</CardContent>
				</Card>
			</div>

			{/* Main Content */}
			<div className="flex gap-6">
				{/* Tree Editor */}
				<div className="flex-1">
					<Card>
						<CardContent className="p-4">
							<DragDropContext onDragEnd={handleDragEnd}>
								{(() => {
									const tree = buildHierarchicalMenu();

									return (
										<Droppable droppableId="sections" type="section">
											{(provided) => (
												<div ref={provided.innerRef} {...provided.droppableProps}>
													<InsertionPoint key="insert-top" order={tree.length > 0 ? tree[0].order - 5 : 10} />
													{tree.map((item, idx) => (
														<Draggable
															key={item.code}
															draggableId={`section-${item.code}`}
															index={idx}
														>
															{(dragProv, dragSnap) => (
																<div
																	ref={dragProv.innerRef}
																	{...dragProv.draggableProps}
																	{...dragProv.dragHandleProps}
																	className={dragSnap.isDragging ? "opacity-75" : ""}
																>
																	{item.isSection ? (
																		<Droppable droppableId={item.code} type="item">
																			{(childProvided) => (
																				<div ref={childProvided.innerRef} {...childProvided.droppableProps}>
																					{renderTreeItem(item, 0)}
																					{childProvided.placeholder}
																				</div>
																			)}
																		</Droppable>
																	) : (
																		renderTreeItem(item, 0)
																	)}
																	<InsertionPoint
																		key={`insert-${idx}`}
																		order={
																			idx < tree.length - 1
																				? Math.floor((item.order + tree[idx + 1].order) / 2)
																				: item.order + 10
																		}
																	/>
																</div>
															)}
														</Draggable>
													))}
													{provided.placeholder}
												</div>
											)}
										</Droppable>
									);
								})()}
							</DragDropContext>
						</CardContent>
					</Card>
				</div>

				{/* Preview Panel */}
				{showPreview && (
					<div className="sticky top-4 self-start">
						<h3 className="text-sm font-medium text-gray-500 mb-2">Live Preview</h3>
						<MenuPreview />
					</div>
				)}
			</div>

			{/* Create Dialog */}
			<Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
				setIsCreateDialogOpen(open);
				if (!open) setCreateDefaults(null);
			}}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Create Menu Item</DialogTitle>
					</DialogHeader>
					<MenuItemForm
						isCreate
						defaults={createDefaults}
						onSubmit={(data) => createMutation.mutate(data)}
						onCancel={() => { setIsCreateDialogOpen(false); setCreateDefaults(null); }}
						availableFeatures={features}
					/>
				</DialogContent>
			</Dialog>

			{/* Edit Dialog */}
			<Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Edit Menu Item</DialogTitle>
					</DialogHeader>
					{editingItem && (
						<MenuItemForm
							item={editingItem}
							onSubmit={(data) =>
								updateMutation.mutate({ code: editingItem.code, data })
							}
							onCancel={() => setEditingItem(null)}
							availableFeatures={features}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
